import { intersection } from 'lodash'
import { default as VueRouter, RawLocation, Route } from 'vue-router/types'
import { IAuth } from 'auth-toolbox'
import { Payload } from '@/store/auth'
import store from '@/store'
import Vue from 'vue'

/**
 * Données associées à une route pour les autorisations d'accès.
 */
export interface IAccessMeta {
  /**
   * Rôles autorisés pour l'accès à un state.
   */
  allowedRoles?: string[]

  /**
   * Autorise les accès anonymes.
   */
  allowAnonymous?: boolean

  /**
   * Fonction permettant d'évaluer l'autorisation d'accès à un state.
   */
  allowFunction?: { (route: Route, auth: IAuth): boolean }

  /**
   * Route de fallback si jamais l'user n'a pas les droits d'acceder a la route
   */
  fallback?: RawLocation
}

/**
 * Service d'accès en fonction de l'utilisateur connecté.
 */
export default class AccessService {
  private auth: IAuth | IAuth
  private router: VueRouter

  /**
   * @param router
   * @param auth Authentication provider.
   */
  constructor (router: VueRouter, auth: IAuth | IAuth) {
    this.router = router
    this.auth = auth

    this.router.beforeEach((to, from, next) => {
      this.checkAllowed(to, from, next)
    })
  }

  /**
   * Obtient le payload de l'utilisateur connecté (JWT), ou null si l'utilisateur n'est pas connecté.
   *
   * @returns {Payload | undefined}
   */
  public getPayload (): Payload | undefined {
    const payload = this.auth.decodeAccessToken()
    return payload
  }

  /**
   * Obtient les rôles de l'utilisateur connecté (JWT), ou bien un tableau vide si l'utilisateur n'est pas connecté.
   *
   * @returns {string[]}
   */
  public getRoles (): string[] {
    let payload = this.getPayload()
    return payload ? payload.roles : []
  }

  /**
   * Vérifie que l'utilisateur est connecté.
   *
   * @returns {boolean}
   */
  public isAuthenticated (): boolean {
    return this.auth.isAuthenticated()
  }

  /**
   * Vérifie l'autorisation d'accès à un state.
   *
   * L'accès est autorisé si allowAnonymous est vrai, ou bien que l'utilisateur est authentifié et possède au moins un
   * rôle présent dans allowedRoles.
   *
   * @param route
   * @returns {boolean}
   */
  public isAllowed (route: Route): boolean {
    let accessData: IAccessMeta = route.meta ? route.meta.access : {}

    if (!accessData) {
      accessData = {}
    }

    let allowFunction = accessData.allowFunction

    if (!allowFunction) {
      allowFunction = () => {
        if (accessData.allowAnonymous) {
          return true
        }
        if (!this.isAuthenticated()) {
          return false
        }
        if (!accessData.allowedRoles) {
          return true
        }
        return intersection(this.getRoles(), accessData.allowedRoles).length > 0
      }
    }

    return allowFunction(route, this.auth)
  }

  public checkAllowed (to: Route, from: Route, next: (to?: (RawLocation | false | ((vm: Vue) => any) | void)) => void) {
    if (!this.isAllowed(to)) {
      if (!from.name && from.path === '/' && !from.matched.length) {
        // This is the initial app loading
        next({ name: 'home', query: { r: to.fullPath } })
      } else {
        next(false)
      }

      let accessData: IAccessMeta = to.meta ? to.meta.access : {}

      if (accessData.fallback) {
        next(accessData.fallback)
        return
      }

      Vue.nextTick(() => {
        store.commit('snackbar/setSnackbarEntry', {
          color: 'error',
          icon: 'mdi-alert-decagram',
          message: `Vous n'avez pas le droit d'accéder à cette page !`
        })
      })
    } else {
      next()
    }
  }
}
